import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Search, Send, Save, ChevronDown, ChevronRight, LogOut, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import MathText from '../components/MathText';
import LaTeXCheatsheet from '../components/LaTeXCheatsheet';
import ImageUpload from '../components/ImageUpload';
import * as reviewerApi from '../api/reviewer';

const EXAMS    = ['qudurat', 'tahsili'];
const SECTIONS = ['math', 'verbal', 'biology', 'chemistry', 'physics'];
// W1 Arabic: reviewer.py accepts an optional validated `language` filter on
// the queue ('' → no filter, server queue statuses unchanged).
const LANGUAGE_OPTIONS = [
  { value: '',   label: 'All languages' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic (AR)' },
];
// Statuses a reviewer may filter within — all part of the server-forced queue.
const QUEUE_STATUS_OPTIONS = [
  { value: '',            label: 'All in queue' },
  { value: 'unreviewed',  label: 'Unreviewed' },
  { value: 'ai_reviewed', label: 'AI Reviewed' },
  { value: 'rejected',    label: 'Bounced back' },
];

function Pill({ children, color = 'gray' }) {
  const colors = {
    gray:   { bg: 'rgba(100,116,139,0.12)', fg: '#475569' },
    green:  { bg: 'rgba(22,163,74,0.12)',   fg: '#15803d' },
    amber:  { bg: 'rgba(180,83,9,0.12)',    fg: '#b45309' },
    violet: { bg: 'rgba(124,58,237,0.12)',  fg: '#7c3aed' },
    red:    { bg: 'rgba(220,38,38,0.12)',   fg: '#dc2626' },
    cyan:   { bg: 'rgba(8,145,178,0.12)',   fg: '#0891b2' },
    gold:   { bg: 'rgba(202,138,4,0.14)',   fg: '#a16207' },
  };
  const c = colors[color] || colors.gray;
  return (
    <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700, background: c.bg, color: c.fg, whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
}

function statusPill(status) {
  if (status === 'unreviewed')  return <Pill color="gray">Unreviewed</Pill>;
  if (status === 'ai_reviewed') return <Pill color="violet">AI Reviewed</Pill>;
  if (status === 'rejected')    return <Pill color="red">Bounced back</Pill>;
  return <Pill color="gray">{status}</Pill>;
}

// Live KaTeX preview shown beneath an editable field. Renders only when the
// text actually contains a $ delimiter, so plain-text fields stay uncluttered.
function FieldPreview({ text }) {
  if (!text || !text.includes('$')) return null;
  return (
    <div style={{ marginTop: 5, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-card-2, rgba(0,0,0,0.03))', border: '1px solid var(--border, rgba(0,0,0,0.06))', fontSize: '0.85rem' }}>
      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px', marginInlineEnd: 8 }}>Preview</span>
      <MathText text={text} />
    </div>
  );
}

// ── Single question editor row ────────────────────────────────────────────────
function QuestionRow({ q, topicsBySection, onReplaced, onRemoved, showFlash }) {
  const [open, setOpen]   = useState(false);
  const [form, setForm]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [version, setVersion] = useState(q.version);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Track the last-focused editable field + caret, so cheatsheet snippets
  // insert at the cursor of whichever field the reviewer was just editing.
  const fieldRefs   = useRef({});
  const lastFocused = useRef(null);

  const registerField = (name) => (el) => { if (el) fieldRefs.current[name] = el; };
  const markFocused   = (name) => () => { lastFocused.current = name; };

  // Cheatsheet click → insert `code` at the caret of the last-focused field
  // (LaTeXCheatsheet also copies it to the clipboard on its own).
  const handleInsert = (code) => {
    const name = lastFocused.current || 'question_text';
    const el   = fieldRefs.current[name];
    setForm(f => {
      if (!f) return f;
      const cur = f[name] ?? '';
      if (el && typeof el.selectionStart === 'number') {
        const s = el.selectionStart, e = el.selectionEnd;
        const next = cur.slice(0, s) + code + cur.slice(e);
        // Restore caret just after the inserted snippet on next tick.
        requestAnimationFrame(() => {
          try { el.focus(); el.setSelectionRange(s + code.length, s + code.length); } catch (_) {}
        });
        return { ...f, [name]: next };
      }
      return { ...f, [name]: cur + code };
    });
  };

  // Initialise the form from the question when first opened.
  const startEditing = () => {
    if (!form) {
      setForm({
        question_text:  q.question_text || '',
        option_a:       q.option_a || '',
        option_b:       q.option_b || '',
        option_c:       q.option_c || '',
        option_d:       q.option_d || '',
        correct_answer: (q.correct_answer || 'a').toLowerCase(),
        hint:           q.hint || '',
        topic:          q.topic || '',
        difficulty:     q.difficulty || '',
        image_url:      q.image_url || null,
      });
    }
    setOpen(o => !o);
  };

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const sectionTopics = topicsBySection[q.section] || [];

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload = {
        version,
        question_text:  form.question_text,
        option_a:       form.option_a,
        option_b:       form.option_b,
        option_c:       form.option_c,
        option_d:       form.option_d,
        correct_answer: form.correct_answer,
        hint:           form.hint,
        topic:          form.topic || null,
        difficulty:     form.difficulty || null,
        image_url:      form.image_url || null,
      };
      const r = await reviewerApi.editQuestion(q.id, payload);
      setVersion(r.question.version);
      showFlash('Saved.');
    } catch (err) {
      if (err?.status === 409) {
        showFlash('Someone else changed this question. Reloading the queue.', 'error');
        onReplaced();
      } else {
        showFlash(err?.error?.message || 'Save failed.', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    if (!window.confirm('Submit this question for admin approval? It will leave your queue.')) return;
    setBusy(true);
    try {
      await reviewerApi.submitQuestion(q.id, version);
      showFlash('Submitted for approval.');
      onRemoved(q.id);
    } catch (err) {
      if (err?.status === 409) {
        showFlash('Someone else changed this question. Reloading the queue.', 'error');
        onReplaced();
      } else {
        showFlash(err?.error?.message || 'Submit failed.', 'error');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ border: '1px solid var(--border, rgba(0,0,0,0.08))', borderRadius: 10, marginBottom: 10, overflow: 'hidden', background: 'var(--surface, #fff)' }}>
      <div
        onClick={startEditing}
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <Pill color="violet">{q.exam}</Pill>
        {q.section && <Pill color="cyan">{q.section}</Pill>}
        {q.language === 'ar' && <Pill color="gold">AR</Pill>}
        {q.source === 'translated' && <Pill color="violet">twin</Pill>}
        {statusPill(q.review_status)}
        <span dir="auto" style={{ flex: 1, fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {q.question_text}
        </span>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>#{q.id}</span>
      </div>

      {open && form && (
        <div style={{ padding: '4px 16px 18px', borderTop: '1px solid var(--border, rgba(0,0,0,0.06))' }}>
          {/* N3r: pair view — the English original this translation came from.
              Read-only reference; the editable Arabic form sits below it. */}
          {q.original && (
            <div style={{ marginTop: 12, border: '1px solid rgba(124,58,237,0.25)', borderRadius: 8, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(124,58,237,0.06)', fontSize: '0.78rem', fontWeight: 700, color: '#7c3aed' }}>
                {q.original.language === 'ar' ? 'ARABIC ORIGINAL · النص الأصلي' : 'ENGLISH ORIGINAL'} · #{q.original.id} · v{q.original.version}
                {q.original_current === false && (
                  <span style={{ marginLeft: 'auto', color: '#dc2626', fontWeight: 700 }}>
                    English edited since translation — re-check against this text
                  </span>
                )}
              </div>
              <div style={{ padding: '12px 14px' }}>
                <div dir={q.original.language === 'ar' ? 'rtl' : 'ltr'} style={{ fontSize: '0.92rem', color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: 10 }}>
                  <MathText text={q.original.question_text} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {['a', 'b', 'c', 'd'].map((l) => {
                    const hit = q.original.correct_answer === l;
                    return (
                      <div key={l} dir={q.original.language === 'ar' ? 'rtl' : 'ltr'} style={{ padding: '6px 10px', borderRadius: 6, fontSize: '0.84rem', background: hit ? 'rgba(21,128,61,0.08)' : 'rgba(0,0,0,0.02)', border: hit ? '1.5px solid #15803d' : '1px solid var(--border, rgba(0,0,0,0.08))', color: hit ? '#15803d' : 'var(--text-secondary)', fontWeight: hit ? 700 : 400 }}>
                        <span style={{ fontWeight: 800, marginRight: 6, textTransform: 'uppercase', fontSize: '0.72rem', opacity: 0.7 }}>{l}</span>
                        <MathText text={q.original[`option_${l}`] || ''} />
                      </div>
                    );
                  })}
                </div>
                {q.original.hint && (
                  <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    <strong style={{ fontSize: '0.7rem', letterSpacing: '0.4px' }}>HINT · </strong>
                    <MathText text={q.original.hint} />
                  </div>
                )}
                <p style={{ margin: '10px 0 0', fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Check the Arabic below against this: same meaning, options in the SAME positions, LaTeX untouched. The answer letter is fixed by the system — if the Arabic options look reordered, fix the texts, never the letter.
                </p>
              </div>
            </div>
          )}
          <div style={{ marginTop: 12, marginBottom: 4 }}>
            <LaTeXCheatsheet onInsert={handleInsert} />
            <p style={{ margin: '6px 0 0', fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Wrap math in <code>$...$</code> (inline) or <code>$$...$$</code> (centered). Click a snippet to copy it and drop it into the field you last edited. A live preview appears under any field that contains math.
            </p>
          </div>

          <div style={{ marginTop: 10, border: '1px solid var(--border, rgba(0,0,0,0.1))', borderRadius: 8, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => setPreviewOpen(p => !p)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', background: 'var(--bg-card-2, rgba(0,0,0,0.03))', border: 'none', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-secondary)' }}
            >
              {previewOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
              Preview as the student sees it
            </button>
            {previewOpen && (
              <div style={{ padding: '14px 16px' }}>
                <div style={{ fontSize: '0.95rem', color: 'var(--text-primary)', lineHeight: 1.6 }}>
                  <MathText text={form.question_text} />
                </div>
                {form.image_url && (
                  <div style={{ marginTop: 12, textAlign: 'center' }}>
                    <img src={form.image_url} alt="Question figure" style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 6 }} />
                  </div>
                )}
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {['a', 'b', 'c', 'd'].map(letter => {
                    const isCorrect = form.correct_answer === letter;
                    return (
                      <div key={letter} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 6, border: `1px solid ${isCorrect ? 'rgba(22,163,74,0.4)' : 'var(--border, rgba(0,0,0,0.08))'}`, background: isCorrect ? 'rgba(22,163,74,0.06)' : 'transparent' }}>
                        <span style={{ fontWeight: 700, color: 'var(--text-muted)', minWidth: 18 }}>{letter.toUpperCase()}</span>
                        <span style={{ flex: 1, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                          <MathText text={form[`option_${letter}`]} />
                        </span>
                        {isCorrect && <Pill color="green">✓ Correct</Pill>}
                      </div>
                    );
                  })}
                </div>
                {form.hint && (
                  <div style={{ marginTop: 14 }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Hint (after a wrong answer)</span>
                    <div style={{ marginTop: 4, fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      <MathText text={form.hint} />
                    </div>
                  </div>
                )}
                <p style={{ margin: '14px 0 0', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Content preview with rendered math, figure, and the marked answer — not a pixel match of the in-game screen. Reflects your unsaved edits live.
                </p>
              </div>
            )}
          </div>

          <div className="form-group" style={{ marginTop: 14 }}>
            <label className="form-label">Question</label>
            <textarea className="form-input" dir="auto" rows={3} value={form.question_text}
              ref={registerField('question_text')} onFocus={markFocused('question_text')}
              onChange={set('question_text')} />
            <FieldPreview text={form.question_text} />
          </div>

          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Figure / diagram (optional)</label>
            <ImageUpload value={form.image_url} onChange={(v) => setForm(f => ({ ...f, image_url: v }))} />
            <p style={{ margin: '4px 0 0', fontSize: '0.74rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
              Attach a figure for questions that reference one (e.g. &quot;the opposite figure&quot;). Saved when you click Save edits.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {['a', 'b', 'c', 'd'].map(letter => (
              <div className="form-group" key={letter} style={{ margin: 0 }}>
                <label className="form-label">Option {letter.toUpperCase()}</label>
                <input className="form-input" dir="auto" value={form[`option_${letter}`]}
                  ref={registerField(`option_${letter}`)} onFocus={markFocused(`option_${letter}`)}
                  onChange={set(`option_${letter}`)} />
                <FieldPreview text={form[`option_${letter}`]} />
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Correct Answer</label>
              <select className="form-input" value={form.correct_answer} onChange={set('correct_answer')}>
                {['a', 'b', 'c', 'd'].map(o => <option key={o} value={o}>{o.toUpperCase()}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Topic</label>
              <select className="form-input" value={form.topic} onChange={set('topic')}>
                <option value="">— None —</option>
                {sectionTopics.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">Difficulty</label>
              <select className="form-input" value={form.difficulty} onChange={set('difficulty')}>
                <option value="">— None —</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          {/* R1: AI review panel. The staging fields (llm_*) are read-only
              suggestions — never auto-applied. The reviewer must click to adopt
              a hint, so nothing reaches a student unread. */}
          {(q.llm_predicted_answer || q.llm_proposed_hint) && (
            <div className="ai-suggestion-panel">
              <div className="ai-suggestion-head">
                <Sparkles size={13} strokeWidth={2.2} />
                <span>AI suggestion · اقتراح الذكاء الاصطناعي</span>
                {q.llm_confidence != null && (
                  <span className="ai-conf">{Math.round(q.llm_confidence * 100)}%</span>
                )}
              </div>

              {q.llm_predicted_answer && (
                <div className={`ai-answer-row${
                  q.llm_predicted_answer.toLowerCase() !== (form.correct_answer || '').toLowerCase()
                    ? ' mismatch' : ''}`}>
                  {q.llm_predicted_answer.toLowerCase() === (form.correct_answer || '').toLowerCase() ? (
                    <span>AI agrees with the current answer ({q.llm_predicted_answer.toUpperCase()}).</span>
                  ) : (
                    <span>
                      <strong>Check this one.</strong> AI answered <strong>{q.llm_predicted_answer.toUpperCase()}</strong>,
                      current answer is <strong>{(form.correct_answer || '?').toUpperCase()}</strong>.
                      Read the note below — when they differ, the note usually contains the correct working.
                    </span>
                  )}
                </div>
              )}

              {q.llm_review_note && (
                <div className="ai-note" dir="auto">{q.llm_review_note}</div>
              )}

              {q.llm_proposed_hint && (
                <div className="ai-hint-block">
                  <div className="ai-hint-label">Proposed hint · تلميح مقترح</div>
                  <div className="ai-hint-text" dir="auto"><MathText text={q.llm_proposed_hint} /></div>
                  <button className="btn btn-ghost btn-sm" type="button"
                    onClick={() => setForm(f => ({ ...f, hint: q.llm_proposed_hint }))}>
                    {form.hint ? 'Replace hint with this' : 'Use this hint'}
                  </button>
                  <span className="ai-hint-note">You can edit it after inserting — your version is final.</span>
                </div>
              )}
            </div>
          )}

          <div className="form-group" style={{ marginTop: 12 }}>
            <label className="form-label">Hint (shown after a wrong answer — never reveals the answer)</label>
            {/* dir="auto" (here and on question/options): Arabic prose displays
                RTL while editing. Raw $...$ LaTeX inside an RTL field can LOOK
                visually reordered — the underlying data is clean; the rendered
                preview below is the source of truth (bidi lesson from Chunk 17). */}
            <textarea className="form-input" dir="auto" rows={2} value={form.hint}
              ref={registerField('hint')} onFocus={markFocused('hint')}
              onChange={set('hint')} />
            <FieldPreview text={form.hint} />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={handleSave}>
              <Save size={14} strokeWidth={2.2} style={{ marginRight: 6 }} />
              {busy ? 'Saving…' : 'Save edits'}
            </button>
            <button className="btn btn-green btn-sm" disabled={busy} onClick={handleSubmit}>
              <Send size={14} strokeWidth={2.2} style={{ marginRight: 6 }} />
              Submit for approval
            </button>
          </div>
          <p style={{ margin: '10px 0 0', fontSize: '0.78rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Save keeps it in your queue. Submit hands it to a DrFahm admin for final approval and removes it from your queue.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function Review() {
  const { user, logout } = useAuth();
  const { i18n } = useTranslation();

  const [questions, setQuestions] = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ exam: '', section: '', review_status: '', search: '', language: '' });
  const [searchInput, setSearchInput] = useState('');
  const [topicsBySection, setTopicsBySection] = useState({});
  const [flash, setFlash] = useState(null);
  const searchTimeout = useRef(null);
  const flashTimeout  = useRef(null);

  const perPage    = 50;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const showFlash = useCallback((msg, type = 'success') => {
    setFlash({ msg, type });
    if (flashTimeout.current) clearTimeout(flashTimeout.current);
    flashTimeout.current = setTimeout(() => setFlash(null), 3500);
  }, []);

  // Load topic taxonomy once for the edit form dropdowns.
  useEffect(() => {
    reviewerApi.getTopics()
      .then(d => {
        const map = {};
        Object.entries(d.taxonomy || {}).forEach(([section, topics]) => {
          map[section] = (topics || []).map(t => ({ key: t.key || t.topic || t, label: t.label || t.key || t }));
        });
        setTopicsBySection(map);
      })
      .catch(() => {});
  }, []);

  const fetchQueue = useCallback(() => {
    setLoading(true);
    reviewerApi.listQueue({ page, per_page: perPage, ...filters })
      .then(d => { setQuestions(d.questions || []); setTotal(d.total || 0); })
      .catch(e => showFlash(e?.error?.message || 'Failed to load the queue.', 'error'))
      .finally(() => setLoading(false));
  }, [page, filters, showFlash]);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const handleFilterChange = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => { handleFilterChange('search', value); }, 400);
  };

  // Optimistically drop a submitted question from the visible list.
  const handleRemoved = (id) => {
    setQuestions(qs => qs.filter(q => q.id !== id));
    setTotal(t => Math.max(0, t - 1));
  };

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '28px 20px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <ClipboardList size={26} strokeWidth={1.8} style={{ color: 'var(--brand-green, #0B5D4B)' }} />
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Question Review</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="lang-toggle"
            onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
            aria-label="Toggle language"
          >
            <span className={i18n.language === 'en' ? 'lang-active' : ''}>EN</span>
            <span className="lang-sep" />
            <span className={i18n.language === 'ar' ? 'lang-active' : ''}>AR</span>
          </button>
          <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{user?.username}</span>
          <button className="btn btn-ghost btn-sm" onClick={logout}><LogOut size={14} strokeWidth={2.2} style={{ marginRight: 6 }} />Log out</button>
        </div>
      </div>
      <p style={{ margin: '0 0 20px', color: 'var(--text-muted)', fontSize: '0.92rem' }}>
        Review questions that have not yet been approved. Edit, then submit for a DrFahm admin to give final approval.
      </p>

      {flash && <div className={`alert alert-${flash.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>{flash.msg}</div>}

      <div className="admin-filter-row" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ position: 'relative' }}>
          <Search size={15} style={{ position: 'absolute', insetInlineStart: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="form-input" style={{ width: 'auto', minWidth: 200, paddingInlineStart: 32 }} placeholder="Search question text…" value={searchInput} onChange={e => handleSearchChange(e.target.value)} />
        </div>
        <select className="form-input" style={{ width: 'auto' }} value={filters.exam} onChange={e => handleFilterChange('exam', e.target.value)}>
          <option value="">All exams</option>
          {EXAMS.map(ex => <option key={ex} value={ex}>{ex.charAt(0).toUpperCase() + ex.slice(1)}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filters.section} onChange={e => handleFilterChange('section', e.target.value)}>
          <option value="">All sections</option>
          {SECTIONS.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filters.review_status} onChange={e => handleFilterChange('review_status', e.target.value)}>
          {QUEUE_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className="form-input" style={{ width: 'auto' }} value={filters.language} onChange={e => handleFilterChange('language', e.target.value)}>
          {LANGUAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '0.92rem' }}>{total} in queue</span>
      </div>

      {loading ? (
        <div className="admin-loading" style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
      ) : questions.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px 20px', color: 'var(--text-muted)' }}>
          <ClipboardList size={40} strokeWidth={1.4} style={{ opacity: 0.5, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>Queue is clear</p>
          <p style={{ margin: '6px 0 0', fontSize: '0.88rem' }}>No questions are waiting for review with these filters.</p>
        </div>
      ) : (
        <div>
          {questions.map(q => (
            <QuestionRow
              key={q.id}
              q={q}
              topicsBySection={topicsBySection}
              onReplaced={fetchQueue}
              onRemoved={handleRemoved}
              showFlash={showFlash}
            />
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="admin-pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 22 }}>
          <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
          <span style={{ color: 'var(--text-secondary)', fontSize: '0.95rem' }}>Page {page} of {totalPages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}